import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { FAIL_URL, SUCCESS_URL } from '@/App';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';
import {
  clearPaymentSession,
  getFailedPaymentsCount,
  getLastPaymentMethod,
  getPaymentProcessLatency,
  getPaymentTimestamps,
  getSystemInfo,
} from '@/screens/Checkout/monitoring';
import { buildUrlWithParams } from '@/utils/queryParams';

interface PaymentResult {
  success: boolean;
  message?: string;
  reason?: string;
  errorCode?: string;
  errorMessage?: string;
  paymentMethod?: string;
  attributes?: Record<string, unknown>;
}

interface PaymentNavigationParams {
  bill?: BillDetailsType;
  isBillError?: boolean;
  brandName: string;
  estimatedCashback: number;
  signupBonusEligible: boolean;
  isItemsSplitActive: boolean;
  numberOfSplits: number;
  payingFor: number;
  setProcessing: (isProcessing: boolean) => void;
}

/**
 * Owns where the checkout goes after a payment attempt: the success URL with all
 * confirmation-screen params baked in, the onResult handler (success / pending
 * timeout / canceled / failure, incl. the purchase_failed gtag event and failure
 * URL), and the pending-timeout fallback — after a pending result, the bill
 * disappearing (404) means the payment actually succeeded on the backend, so it
 * navigates to success instead of leaving the user on a "no bill" screen.
 */
export const usePaymentNavigation = ({
  bill,
  isBillError,
  brandName,
  estimatedCashback,
  signupBonusEligible,
  isItemsSplitActive,
  numberOfSplits,
  payingFor,
  setProcessing,
}: PaymentNavigationParams) => {
  const navigate = useNavigate();
  const { queryParams } = useValidateQueryParams();
  const { outletId, brandId, tableNumber, counterNumber } = queryParams();
  const pendingTimeoutFiredRef = useRef(false);

  const successUrl = useMemo(() => {
    if (bill) {
      const { orderNumber, orderId } = bill;

      // An items split has no numberOfSplits/payingFor — it carries its type instead
      const splitParams = isItemsSplitActive
        ? { splitType: 'split_by_items' }
        : { numberOfSplits, payingFor };

      // Common params for both counter and table orders
      const commonParams = {
        order: orderId,
        orderNumber,
        brand: brandName,
        cashback: estimatedCashback,
        outletId,
        brandId,
        ...splitParams,
        splitBillEnabled: true,
        orderValue: bill.baseTotal,
        serviceFeeAmount: bill.serviceFee,
        isServiceFeeEnabled: true,
        bonus: signupBonusEligible ? '1' : '0',
      };

      // Add counter or table number
      const params = counterNumber
        ? { ...commonParams, counterNumber }
        : { ...commonParams, tableNumber };

      return buildUrlWithParams(SUCCESS_URL, params);
    }
    return SUCCESS_URL;
  }, [
    bill,
    tableNumber,
    counterNumber,
    brandId,
    outletId,
    brandName,
    estimatedCashback,
    numberOfSplits,
    payingFor,
    isItemsSplitActive,
    signupBonusEligible,
  ]);

  const onPaymentResult = useCallback(
    (result: PaymentResult) => {
      if (result?.success) {
        // Add small delay to ensure monitoring callback saves payment method
        setTimeout(() => {
          const paymentMethodCode = getLastPaymentMethod();
          const urlWithPaymentMethod = paymentMethodCode
            ? `${successUrl}&paymentMethod=${paymentMethodCode}`
            : successUrl;

          clearPaymentSession();
          navigate(urlWithPaymentMethod);
        }, 100);
        return;
      }

      if (result.reason === 'pending_timeout') {
        pendingTimeoutFiredRef.current = true;
        setProcessing(false);
        return;
      }

      // Canceled — user closed 3DS / modal, return to idle without navigating to failure
      if (result.reason === 'canceled') {
        setProcessing(false);
        return;
      }

      if (typeof window.gtag === 'function') {
        const paymentMethodCode = getLastPaymentMethod();
        window.gtag('event', 'purchase_failed', {
          sourceScreen: 'checkout',
          paymentMethod: paymentMethodCode || 'unknown',
          errorCode: result.errorCode || 'unknown',
          errorMessage: result.message || 'Payment failed',
          orderId: bill?.orderId || bill?.orderNumber,
          brandName: brandName,
          order_value: bill?.baseTotal || 0,
          service_fee_amount: bill?.serviceFee || 0,
          split_bill_enabled: numberOfSplits > 0 || isItemsSplitActive,
          ...(isItemsSplitActive
            ? { split_type: 'split_by_items' }
            : {
                number_of_splits: numberOfSplits || 0,
                paying_for: payingFor || 0,
              }),
          numberOfFailedPaymentsInARow: getFailedPaymentsCount(),
          ...getPaymentTimestamps(),
          paymentProcessLatency: getPaymentProcessLatency(),
          ...getSystemInfo(),
        });
      }

      const paymentMethodCode = getLastPaymentMethod();
      const failureUrl = buildUrlWithParams(FAIL_URL, {
        message: result.message || 'Payment failed',
        errorCode: result.errorCode,
        paymentMethod: paymentMethodCode,
        order: bill?.orderNumber,
        orderId: bill?.orderId,
        orderValue: bill?.baseTotal,
        brand: brandName,
        splitBillEnabled: numberOfSplits > 0 || isItemsSplitActive ? 'true' : undefined,
        numberOfSplits: numberOfSplits > 0 ? numberOfSplits : undefined,
        payingFor: !isItemsSplitActive && payingFor > 0 ? payingFor : undefined,
        splitType: isItemsSplitActive ? 'split_by_items' : undefined,
        serviceFeeAmount: bill?.serviceFee,
        isServiceFeeEnabled: 'true',
      });

      clearPaymentSession();
      navigate(failureUrl, { replace: true });
    },
    [
      navigate,
      successUrl,
      bill,
      brandName,
      numberOfSplits,
      payingFor,
      isItemsSplitActive,
      setProcessing,
    ]
  );

  // After a pending timeout, if the bill disappears (404) the payment succeeded on the backend.
  // Navigate to success instead of showing the "no bill" screen.
  useEffect(() => {
    if (isBillError && pendingTimeoutFiredRef.current) {
      pendingTimeoutFiredRef.current = false;
      navigate(successUrl);
    }
  }, [isBillError, navigate, successUrl]);

  return { onPaymentResult };
};
